// pages/Services.js
import React from "react";
import {
  Box, Container, Typography, Grid, Card, CardContent,
  List, ListItem, ListItemIcon, ListItemText,
  Accordion, AccordionSummary, AccordionDetails,
  Chip, Paper,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SchoolIcon      from "@mui/icons-material/School";
import GroupsIcon      from "@mui/icons-material/Groups";
import CodeIcon        from "@mui/icons-material/Code";
import HandshakeIcon   from "@mui/icons-material/Handshake";
import CloudIcon       from "@mui/icons-material/Cloud";
import SecurityIcon    from "@mui/icons-material/Security";
import DataObjectIcon  from "@mui/icons-material/DataObject";
import AutoGraphIcon   from "@mui/icons-material/AutoGraph";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import ExpandMoreIcon  from "@mui/icons-material/ExpandMore";
import PhoneIcon       from "@mui/icons-material/Phone";
import { useInView }   from "react-intersection-observer";
import { keyframes }   from "@mui/system";

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-50px); }
  to   { opacity: 1; transform: translateX(0); }
`;

// Blue palette
const BLUE  = "#1565C0";
const DARK  = "#0D47A1";
const LIGHT = "#1976D2";

const Services = () => {
  const { ref: servicesRef, inView: servicesInView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const { ref: faqRef,      inView: faqInView }      = useInView({ triggerOnce: true, threshold: 0.1 });

  const serviceDetails = [
    { icon: <SchoolIcon sx={{ fontSize: 60 }} />,      title: "Corporate Training Programs",    description: "Transform your team with industry-leading technology training", features: ["Full-Stack JavaScript", "Cloud Computing", "AI/ML Fundamentals", "DevOps & CI/CD"], benefits: ["Hands-on project-based learning"] },
    { icon: <GroupsIcon sx={{ fontSize: 60 }} />,      title: "IT Staffing Solutions",          description: "Access pre-vetted tech talent for your critical projects",    features: ["Contract-to-Hire", "Permanent Staffing", "Team Augmentation", "Remote Teams"],   benefits: ["98% candidate retention rate"] },
    { icon: <CodeIcon sx={{ fontSize: 60 }} />,        title: "Custom Software Development",    description: "End-to-end software solutions built with modern technologies",  features: ["Web Applications", "Mobile Apps", "Progressive Web Apps", "SaaS Products"],    benefits: ["Agile development methodology"] },
    { icon: <CloudIcon sx={{ fontSize: 60 }} />,       title: "Cloud Solutions & Migration",    description: "Seamless cloud transformation and infrastructure management",   features: ["AWS/Azure/GCP Migration", "Kubernetes", "Serverless", "Cost Optimization"],   benefits: ["Up to 40% cost reduction"] },
    { icon: <DataObjectIcon sx={{ fontSize: 60 }} />,  title: "API Development & Integration",  description: "Robust APIs and seamless third-party integrations",             features: ["RESTful APIs", "GraphQL", "Payment Gateways", "CRM Integration"],              benefits: ["Comprehensive documentation"] },
    { icon: <SecurityIcon sx={{ fontSize: 60 }} />,    title: "Cybersecurity Consulting",       description: "Protect your business with enterprise-grade security",          features: ["Security Audits", "Penetration Testing", "Compliance (GDPR/HIPAA)", "Data Encryption"], benefits: ["Certified professionals"] },
    { icon: <AutoGraphIcon sx={{ fontSize: 60 }} />,   title: "Data Analytics & BI",            description: "Transform data into actionable business insights",              features: ["BI Dashboards", "ETL Pipelines", "Predictive Analytics", "Big Data"],           benefits: ["Real-time insights"] },
    { icon: <HandshakeIcon sx={{ fontSize: 60 }} />,   title: "IT Consulting & Strategy",       description: "Strategic technology guidance for digital transformation",      features: ["Digital Roadmap", "Architecture Review", "Vendor Selection", "ROI Analysis"],   benefits: ["Experienced consultants"] },
    { icon: <SupportAgentIcon sx={{ fontSize: 60 }} />, title: "Maintenance & Support",          description: "24/7 technical support and application maintenance",            features: ["24/7 Support", "Bug Fixes", "Performance Monitoring", "SLA-based"],            benefits: ["< 1 hour response time"] },
    { icon: <IntegrationInstructionsIcon sx={{ fontSize: 60 }} />, title: "DevOps & Automation", description: "Streamline development with CI/CD and automation",             features: ["CI/CD Pipelines", "Infrastructure as Code", "Docker/Kubernetes", "GitOps"],    benefits: ["50% faster deployment"] },
  ];

  const faqs = [
    { question: "What is your typical project timeline?",     answer: "Timelines vary by scope. Small projects: 2-3 months, medium: 3-6 months, large enterprise: 6-12 months. We work in 2-week sprints with regular updates." },
    { question: "Do you offer post-launch support?",          answer: "Yes! All development projects include a 6-month warranty plus comprehensive maintenance packages with 24/7 availability, bug fixes, and performance optimization." },
    { question: "Can you work with our existing dev team?",   answer: "Absolutely! We excel at team augmentation and seamlessly integrate with your team, following your processes, tools, and workflows." },
    { question: "What industries do you specialize in?",      answer: "We have deep experience across fintech, healthcare, e-commerce, logistics, education, and SaaS — bringing industry-specific knowledge to every project." },
    { question: "How do you ensure code quality?",            answer: "We follow best practices including code reviews, automated testing, CI, and coding standards. Every project includes comprehensive QA." },
    { question: "What are your payment terms?",               answer: "Milestone-based: 30% upfront, 40% at mid-project milestone, 30% upon completion. Monthly billing available for longer engagements." },
  ];

  return (
    <Box>
      {/* ── Hero ── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${DARK} 0%, ${BLUE} 50%, ${LIGHT} 100%)`,
          py: 15, mt: 8, position: "relative", overflow: "hidden",
          "&::before": {
            content: '""', position: "absolute",
            top: "-50%", right: "-10%",
            width: "500px", height: "500px",
            background: "radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)",
            borderRadius: "50%",
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", color: "white", position: "relative", zIndex: 1 }}>
            <Typography variant="overline" sx={{ fontSize: "1rem", fontWeight: 700, letterSpacing: "0.1em", mb: 2, display: "block" }}>
              COMPREHENSIVE SOLUTIONS
            </Typography>
            <Typography variant="h1" sx={{ fontSize: { xs: "2.5rem", md: "4rem" }, fontWeight: 800, mb: 3 }}>
              Our Services
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: "800px", mx: "auto", opacity: 0.93, lineHeight: 1.8 }}>
              From training your team to building cutting-edge software, we provide end-to-end technology solutions that drive business growth and innovation
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ── Service Cards ── */}
      <Container maxWidth="xl" sx={{ py: 10 }} ref={servicesRef}>
        <Grid container spacing={4} justifyContent="center" alignItems="stretch">
          {serviceDetails.map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: "flex", justifyContent: "center" }}>
              <Card
                sx={{
                  width: "100%", maxWidth: 360, minWidth: 300,
                  minHeight: 340, display: "flex", flexDirection: "column",
                  borderRadius: 3,
                  boxShadow: "0 2px 12px rgba(13,71,161,0.08)",
                  transition: "all 0.4s cubic-bezier(0.4,0,0.2,1)",
                  opacity: servicesInView ? 1 : 0,
                  transform: servicesInView ? "translateY(0)" : "translateY(30px)",
                  transitionDelay: `${index * 0.08}s`,
                  borderTop: `4px solid ${BLUE}`,
                  "&:hover": { boxShadow: `0 12px 48px rgba(13,71,161,0.2)`, transform: "translateY(-8px)" },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ background: "rgba(13,71,161,0.08)", color: BLUE, width: 64, height: 64, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
                    {service.icon}
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 700, color: "#0A1929" }}>{service.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{service.description}</Typography>
                  <List>
                    {service.features.slice(0, 3).map((feature, i) => (
                      <ListItem key={i} disablePadding sx={{ mb: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CheckCircleIcon sx={{ fontSize: 18, color: BLUE }} />
                        </ListItemIcon>
                        <ListItemText primary={feature} primaryTypographyProps={{ fontSize: "0.95rem" }} />
                      </ListItem>
                    ))}
                  </List>
                  <Box sx={{ mt: 2 }}>
                    <Chip label={service.benefits[0]} size="small" sx={{ bgcolor: "rgba(13,71,161,0.08)", color: BLUE, fontWeight: 600 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── FAQ ── */}
      <Container maxWidth="md" sx={{ py: 10 }} ref={faqRef}>
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography variant="overline" sx={{ color: BLUE, fontWeight: 700, fontSize: "1rem", letterSpacing: "0.1em" }}>FAQS</Typography>
          <Typography variant="h2" sx={{ mt: 2, mb: 2, color: "#0A1929" }}>Frequently Asked Questions</Typography>
          <Typography variant="h6" color="text.secondary">Got questions? We've got answers</Typography>
        </Box>
        {faqs.map((faq, index) => (
          <Accordion
            key={index}
            sx={{
              mb: 2, boxShadow: "none",
              border: "1px solid rgba(13,71,161,0.1)",
              borderRadius: "12px !important",
              opacity: faqInView ? 1 : 0,
              animation: faqInView ? `${slideIn} 0.5s ease-out ${index * 0.1}s both` : "none",
              "&:before": { display: "none" },
              "&.Mui-expanded": { boxShadow: "0 4px 20px rgba(13,71,161,0.12)", border: `1px solid rgba(13,71,161,0.2)` },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: BLUE }} />}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#0A1929" }}>{faq.question}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>{faq.answer}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Container>

      {/* ── CTA ── */}
      <Box sx={{ bgcolor: "#F5F9FF", py: 8 }}>
        <Container maxWidth="md">
          <Paper
            sx={{
              p: 6, textAlign: "center",
              background: `linear-gradient(135deg, ${DARK} 0%, ${LIGHT} 100%)`,
              color: "white", borderRadius: 4,
              boxShadow: "0 8px 40px rgba(13,71,161,0.3)",
            }}
          >
            <PhoneIcon sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h3" sx={{ mb: 2, fontWeight: 700 }}>Ready to Get Started?</Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.93 }}>
              Let's discuss your project requirements and create a custom solution for your business
            </Typography>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
              <Chip label="📞 +1 (945) 278-6092"      sx={{ bgcolor: "white", color: DARK, px: 3, py: 3, fontSize: "1rem", fontWeight: 700 }} />
              <Chip label="✉️ hr@quantumitai.com"      sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "white", px: 3, py: 3, fontSize: "1rem", fontWeight: 600, border: "1px solid rgba(255,255,255,0.3)" }} />
            </Box>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default Services;
